import styled from "@emotion/styled";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Stack } from "@mui/system";
import { getValueColorForRenderingInputOnChange } from "features/shared/lib/color";
import { getValueForRenderingInputOnChange } from "features/shared/lib/rendering";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { DropdownSingleSelect, IconButton } from ".";
import { sortFunction } from "../functions/general";
import { getSortType, isCellTypeDownload } from "../functions/TechnicalData";
import { getTableTypicalColumn } from "../utils/components";
import { updateTableCell } from "../utils/dataManipulation";
import { cn } from "../utils/styling";
import { AutocompleteField } from "./AutocompleteField";
import { Button } from "./ButtonNew";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { Icon } from "./Icon";
import { TrashIcon } from "./IconsTextsUtils";
import { ColorInput, LabelLessInput } from "./Input";

const TableHeaderCell = ({ className, columnsMinWidth, endAndromat, handleColumnClick, label, labelClassName }) => {
  return (
    <TableCell
      className={cn("h-9 p-0 pl-3 pr-1 ", className)}
      onClick={handleColumnClick}
      sx={{
        ":active": {
          background: "var(--core-color-secondary-blue-50)",
          border: "1px solid var(--core-color-secondary-blue-300)",
        },
        ":hover": {
          boxShadow: "var(--box-shadow-up-hover)",
        },
        background: "var(--core-color-grey-50)",
        border: "var(--border-subtle)",
        minWidth: columnsMinWidth,
      }}
      tabIndex={1}
    >
      <Box className="flex items-center justify-between">
        <span className={cn("text-lds-foreground-primary font-ds-body-regular-xs ", labelClassName)}>{label}</span>
        {endAndromat}
      </Box>
    </TableCell>
  );
};

const ActionHeaderCell = () => {
  const { t } = useTranslation();
  return (
    <TableHeaderCell
      className="sticky right-0 top-0 z-table_sticky_header"
      columnsMinWidth="8rem"
      label={t("Components.table.columns.actions")}
    />
  );
};

const TableHeader = ({
  columnsMinWidth,
  handleColumnClick,
  hasActionsColumn,
  headers,
  isChildTable = false,
  isSortAscending,
  sortColumn,
}) => {
  return (
    <TableHead>
      <TableRow>
        {headers?.map((header, index) => {
          const sortType = getSortType(header.dataKey, sortColumn, isSortAscending);
          const isDownload = isCellTypeDownload(header.cellType);
          const canSort = !isDownload && header.disableSort !== true;

          return (
            <TableHeaderCell
              className={cn(
                index === 0 && "sticky left-0 top-0",
                index === 0 && !isChildTable && "z-table_sticky_header",
                index === 0 && isChildTable && "z-table_sticky_child",
                canSort || !isDownload ? "cursor-pointer" : "",
                header.headerCellClassName
              )}
              columnsMinWidth={header.minWidth ? header.minWidth : columnsMinWidth}
              endAndromat={canSort ? <Icon iconName={sortType} size="sm" svgClassName="scale-150" /> : null}
              handleColumnClick={canSort ? () => handleColumnClick(header.dataKey) : undefined}
              key={index}
              label={header.label}
              labelClassName={header.headerClassName}
            />
          );
        })}
        {hasActionsColumn && <ActionHeaderCell />}
      </TableRow>
    </TableHead>
  );
};

export const Table = (props) => {
  const {
    addable,
    childTableDataKey = null,
    className,
    columnsMinWidth,
    containedScroll = false,
    data,
    editable = () => false,
    foldTableOnChangeOfVariable,
    headers,
    idColumnName,
    isChildTable = false,
    newRowDataKey,
    onAddNewRow,
    onCellLeave,
    onExpand,
    onRowClick,
    rowHeight,
    rowRemoveable = () => false,
    scrollDown,
  } = props;
  const isSomeRowRemovable = data?.some((row) => rowRemoveable?.(row));
  const hasActionsColumn = !!(props.onCopy || props.onDelete || props.onEdit);

  const [tableData, setTableData] = useState([]);
  const [isSortAscending, setIsSortAscending] = useState(true);
  const [sortColumn, setSortColumn] = useState(null);

  const sortData = (data) => {
    if (!sortColumn) return data;
    const sortedData = [...data];
    if (isSortAscending) {
      sortedData?.sort((a, b) => sortFunction(a[sortColumn], b[sortColumn]));
    } else {
      sortedData?.sort((a, b) => sortFunction(b[sortColumn], a[sortColumn]));
    }
    return sortedData;
  };

  const hasChild = data?.some((row) => row[childTableDataKey]?.length > 0);

  const handleCellValueChange = (e, rowId, dataKey, idColumnName) => {
    const newValue = e.target.value;
    const updatedData = updateTableCell(tableData, rowId, dataKey, newValue, idColumnName);
    setTableData(updatedData);
  };

  const emptyNewRow = { id: uuidv4(), [newRowDataKey]: null };
  useEffect(() => {
    setNewRow(emptyNewRow);
    setTableData(sortData(data));
  }, [sortColumn, isSortAscending, data]);

  useEffect(() => setSelectedRow(null), [foldTableOnChangeOfVariable]);

  const [selectedRow, setSelectedRow] = useState(null);

  const handleRowClick = (id) => {
    setSelectedRow(id === selectedRow ? null : id);
    onExpand?.();
  };

  const handleColumnClick = (header) => {
    if (header === sortColumn) setIsSortAscending(!isSortAscending);
    else setIsSortAscending(true);
    setSortColumn(header);
  };

  const [newRow, setNewRow] = useState(emptyNewRow);
  const handleNewRowCellChange = (e) => {
    const newValue = e.target.value;
    setNewRow((prev) => ({ ...prev, [newRowDataKey]: newValue }));
  };

  const sRef = useRef();
  useEffect(() => {
    if (scrollDown) sRef.current?.scrollIntoView();
  }, [data]);

  return (
    <>
      <Wrapper
        className={className}
        containedScroll={containedScroll}
        rowHeight={rowHeight}
        rowRemoveable={isSomeRowRemovable}
      >
        <MuiTable size="small" stickyHeader={!isChildTable}>
          <TableHeader
            columnsMinWidth={columnsMinWidth}
            handleColumnClick={handleColumnClick}
            hasActionsColumn={hasActionsColumn}
            headers={headers}
            isChildTable={isChildTable}
            isSortAscending={isSortAscending}
            sortColumn={sortColumn}
          />
          <TableBody onMouseLeave={onCellLeave}>
            {tableData?.map((row, index) => {
              const childData = row[childTableDataKey] || [];
              const isNestedTable = !!childTableDataKey;
              const rowHasChild = (childData?.length > 0 || addable) && isNestedTable;

              if (!rowHasChild || isChildTable)
                return (
                  <Row
                    {...props}
                    editable={editable(row)}
                    hasActionsColumn={hasActionsColumn}
                    key={index}
                    onCellChange={handleCellValueChange}
                    onRowClick={onRowClick ? () => onRowClick(row) : undefined}
                    parentHasChild={hasChild}
                    parentRowId={props?.parentRowId ?? row[idColumnName]}
                    row={row}
                    rowHasChild={rowHasChild}
                    rowIndex={index}
                    rowRemoveable={rowRemoveable(row)}
                  />
                );
              return (
                <NestedRow
                  {...props}
                  childData={[...childData]}
                  editable={editable(row)}
                  hasActionsColumn={hasActionsColumn}
                  key={index}
                  onCellChange={handleCellValueChange}
                  onExpandClick={() => handleRowClick(row[idColumnName])}
                  onRowClick={() => {
                    onRowClick?.(row);
                  }}
                  parentHasChild={hasChild}
                  row={row}
                  rowHasChild={rowHasChild}
                  rowIndex={index}
                  rowRemoveable={rowRemoveable(row)}
                  selectedRow={selectedRow}
                />
              );
            })}
          </TableBody>
        </MuiTable>
        {!sortColumn && <div ref={sRef} />}
      </Wrapper>
      {addable && (
        <Wrapper
          className={cn("mt-2", isChildTable && "!pl-9")}
          rowHeight={rowHeight}
          rowRemoveable={isSomeRowRemovable}
        >
          <MuiTable
            style={{
              border: "2px solid #ddd",
            }}
          >
            <TableBody>
              <Row
                {...props}
                className=""
                editable={true}
                headers={[
                  getTableTypicalColumn({
                    dataKey: newRowDataKey,
                    label: headers?.find((header) => header.dataKey === newRowDataKey)?.label,
                  }),
                ]}
                idColumnName="id"
                onBlur={({ value, ...rest }) => {
                  if (value !== "" && value !== null && value !== undefined) onAddNewRow({ ...rest, value });
                }}
                onCellChange={handleNewRowCellChange}
                parentRowId={-1}
                row={newRow}
                rowHasChild={false}
                rowRemoveable={false}
              />
            </TableBody>
          </MuiTable>
        </Wrapper>
      )}
    </>
  );
};

function NestedRow(props) {
  const {
    childConfirmDelete,
    childData,
    childEditable,
    childHeaders,
    childIdColumnName,
    childNewRowDataKey,
    childOnAddNewRow,
    childOnBlur,
    childOnDelete,
    childRowRemoveable,
    hasActionsColumn,
    idColumnName,
    row,
    rowRemoveable,
    selectedRow,
  } = props;
  return (
    <>
      <Row {...props} parentRowId={row[idColumnName]} />
      <TableRow>
        <TableCell className="m-0 p-0" colSpan={Object.keys(row)?.length + (hasActionsColumn ? 1 : 0)}>
          <Collapse in={selectedRow === row[idColumnName]} timeout="auto" unmountOnExit>
            <Box className="my-6 mr-6">
              <Table
                {...props}
                childTableDataKey={undefined}
                className="pl-9"
                confirmDelete={childConfirmDelete}
                data={childData}
                editable={childEditable}
                headers={childHeaders}
                idColumnName={childIdColumnName ?? idColumnName}
                isChildTable={true}
                newRowDataKey={childNewRowDataKey}
                onAddNewRow={(rest) => childOnAddNewRow({ parentRow: row, ...rest })}
                onBlur={(rest) => childOnBlur({ parentRow: row, ...rest })}
                onDelete={childOnDelete ? (rest) => childOnDelete({ parentRow: row, ...rest }) : undefined}
                parentRowId={row[idColumnName]}
                // editable={() => editable}
                rowRemoveable={(childRow) => childRowRemoveable?.(childRow) ?? rowRemoveable}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const Row = ({
  confirmDelete,
  data,
  disableRowHover,
  editable,
  hasActionsColumn,
  headers,
  idColumnName,
  onBlur,
  onCellChange,
  onCopy,
  onDelete,
  onEdit,
  onExpandClick,
  onRowClick,
  parentHasChild,
  parentRowId,
  row,
  rowHasChild,
  rowIndex,
  rowRemoveable,
  selectedRow,
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const rowId = row[idColumnName];
  return (
    <TableRow
      className={cn(`relative ${onRowClick && "cursor-pointer"}`)}
      onClick={onRowClick}
      sx={
        disableRowHover
          ? undefined
          : {
              ":hover": {
                background: "var(--color-selection-subtle)",
              },
            }
      }
    >
      {headers?.map((header, index) => {
        const value = header.valueTransformation?.(row);
        const subValue = header.subValueTransformation?.(row);
        const placeholder = header.placeholder || header.label;
        const href = header.href?.(row);
        const errorMessage = (input) => header.errorTransformation && header.errorTransformation(row, data, input);
        const downloadZipFile = () => header.downloadZipFile?.(row);
        const beforeTextIconName =
          index !== 0 || !rowHasChild ? null : selectedRow === row[idColumnName] ? "ChevronDown" : "ChevronRight";
        const fieldName = header.dataKey;
        const cellIsReadOnly = typeof header?.isReadOnly === "function" ? header.isReadOnly(row) : header?.isReadOnly;
        const cellType = typeof header.cellType === "function" ? header.cellType(row) : header.cellType;
        const onCellClick = header.onCellClick;
        const resolvedValueClassName =
          typeof header.valueClassName === "function"
            ? header.valueClassName(row, rowIndex, index)
            : header.valueClassName;
        const valueComponentProps = header.valueComponentProps;
        return (
          <CustomCell
            beforeTextIconName={rowHasChild && beforeTextIconName}
            cellType={cellType}
            contentType={header.contentType}
            displayOptions={header.displayOptions && header.displayOptions?.(row)}
            downloadZipFile={downloadZipFile}
            editable={editable && !cellIsReadOnly}
            errorMessage={errorMessage}
            href={href}
            iconAction={onExpandClick}
            isEditing={editingCell && rowId === editingCell.rowId && fieldName === editingCell.fieldName}
            key={index}
            onBlur={() => {
              if (onBlur) onBlur({ fieldName, parentRowId, row, rowId, value });
              setEditingCell(null);
            }}
            onCellChange={(e) => {
              if (onCellChange) onCellChange(e, rowId, fieldName, idColumnName);
              if (["autocomplete", "color", "dropdown"].includes(header.cellType) && onBlur)
                onBlur({
                  fieldName,
                  parentRowId,
                  row,
                  rowId,
                  value: e.target.value,
                });
            }}
            onCellClick={() => {
              onCellClick?.(row);
            }}
            onClick={() => setEditingCell({ fieldName, row, rowId })}
            onMouseEnter={() => header.onCellEnter?.(row)}
            options={header.options && header.options(row)}
            placeholder={placeholder}
            subValue={subValue}
            subValueClassName="font-ds-body-regular-xxs text-lds-foreground-muted"
            value={value}
            valueClassName={cn(
              "body-cell font-ds-body-regular-md",
              index === 0 && "sticky left-0 z-table_sticky",
              index === 0 && parentHasChild && !rowHasChild && "pl-11",
              onCellClick && "cursor-pointer text-lds-link-primary",
              resolvedValueClassName
            )}
            valueComponentProps={valueComponentProps}
          />
        );
      })}
      {hasActionsColumn && (
        <RowActionCell
          confirmDelete={confirmDelete}
          deleteDisabled={!rowRemoveable}
          editable={editable}
          onCopy={onCopy ? () => onCopy({ parentRowId, row, rowId }) : undefined}
          onDelete={onDelete ? () => onDelete({ parentRowId, row, rowId }) : undefined}
          onEdit={onEdit ? () => onEdit({ parentRowId, row, rowId }) : undefined}
        />
      )}
    </TableRow>
  );
};

const AutocompleteCell = ({ editable, onCellChange, options = [], placeholder, value, valueComponentProps = {} }) => {
  const [inputValue, setInputValue] = useState(undefined);
  const { freeSolo = true, ...autocompleteProps } = valueComponentProps;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const inputValueForRendering = getValueForRenderingInputOnChange(inputValue);
  return (
    <AutocompleteField
      className="w-full"
      disabled={!editable}
      freeSolo={freeSolo}
      inputValue={inputValueForRendering}
      onBlur={() => {
        if (inputValue !== value) onCellChange({ target: { value: inputValue } });
      }}
      onChange={(_, value) => {
        onCellChange({ target: { value } });
      }}
      onInputChange={(_, newValue) => {
        setInputValue(newValue);
      }}
      options={options}
      placeholder={placeholder}
      {...autocompleteProps}
      isOptionEqualToValue={(a, b) => a === b}
      value={inputValueForRendering}
    />
  );
};

const DownloadCell = ({ cellType, href, onCellClick, value, valueClassName, valueComponentProps }) => {
  return (
    <TableCell
      className={cn("hover:bg-lds-selection-strong", valueClassName)}
      onClick={onCellClick}
      {...valueComponentProps}
    >
      {cellType === "downloadFile" && (
        <a className=" flex cursor-pointer justify-center" href={href} rel="noreferrer" target="_blank">
          {value}
        </a>
      )}
      {cellType === "downloadZipFile" && <div className=" flex justify-center">{value}</div>}
    </TableCell>
  );
};

const RowActionCell = ({ confirmDelete = true, deleteDisabled, editable, onCopy, onDelete, onEdit }) => {
  const [showModal, setShowModal] = useState(false);
  const handleActionClick = (event, action) => {
    event.stopPropagation();
    action?.();
  };

  return (
    <TableCell className="body-cell min-w-32 sticky right-0 z-table_sticky w-32">
      <DeleteConfirmModal
        onCancel={() => setShowModal(false)}
        onConfirm={() => {
          setShowModal(false);
          onDelete?.();
        }}
        open={showModal}
      />
      <Stack className="h-full w-full justify-center gap-1">
        {onCopy && (
          <IconButton
            buttonProps={{
              className: "h-8 w-8",
              onClick: (event) => handleActionClick(event, onCopy),
              variant: "tertiary",
            }}
            iconProps={{ iconName: "Duplicate", size: "md" }}
          />
        )}
        {onEdit && (
          <IconButton
            buttonProps={{
              className: "h-8 w-8",
              disabled: !editable,
              onClick: (event) => handleActionClick(event, onEdit),
              variant: "tertiary",
            }}
            iconProps={{ iconName: "Settings", size: "sm" }}
          />
        )}
        {onDelete && (
          <TrashIcon
            buttonProps={{
              disabled: deleteDisabled,
              onClick: (event) => {
                event.stopPropagation();
                if (!deleteDisabled) {
                  if (!confirmDelete) onDelete();
                  else setShowModal(true);
                }
              },
            }}
            iconProps={{ size: "md" }}
          />
        )}
      </Stack>
    </TableCell>
  );
};

const TextCellContent = ({
  contentType,
  editable,
  errorMessage,
  onBlur,
  onCellChange,
  onClick,
  placeholder,
  value,
  valueComponentProps,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") event.target.blur();
  };
  const handleBlur = () => {
    setIsEditing(false);
    if (onBlur) onBlur();
  };
  const handleClick = () => {
    setIsEditing(true);
    if (onClick) onClick();
  };
  const [error, setError] = useState("");
  const isValueNull = value === undefined || value === null;
  return (
    <>
      {editable && isEditing && (
        <div className="w-full">
          <LabelLessInput
            autoFocus={true}
            className="block w-full"
            height="3.2rem"
            onBlur={handleBlur}
            onChange={(e) => {
              if (onCellChange) onCellChange(e);
              if (errorMessage) setError(errorMessage(e.target.value));
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              marginLeft: "-6px",
            }}
            type={contentType ?? "text"}
            value={!isValueNull ? value : ""}
            {...valueComponentProps}
          />
          {error && <p className="text-lds-purpose-status-negative-subtle font-ds-body-regular-xs">{error}</p>}
        </div>
      )}
      {editable && !isEditing && (
        <Button
          className="w-full !text-lds-foreground-primary"
          onClick={handleClick}
          size="sm"
          style={{
            cursor: "text",
            height: "auto",
            justifyContent: "flex-start",
            marginLeft: "-6px",
            minHeight: "3rem",
          }}
          variant="tertiary"
        >
          {t(value)}
        </Button>
      )}
      {!editable && <span {...valueComponentProps}>{value}</span>}
    </>
  );
};

const ColorCell = ({ onCellChange, value }) => {
  const { t } = useTranslation();
  const [color, setColor] = useState(value);

  useEffect(() => {
    setColor(value);
  }, [value]);

  const valueColor = getValueColorForRenderingInputOnChange(color);

  return (
    <Stack className="w-full min-w-[10rem] flex-row items-center gap-2" onClick={(event) => event.stopPropagation()}>
      <ColorInput
        className="w-7"
        color={valueColor}
        onBlur={() => {
          onCellChange?.({ target: { value: color } });
        }}
        setColor={setColor}
      />
      <span className="text-lds-foreground-muted font-ds-body-regular-xxs">
        {color || t("Components.table.values.noColor")}
      </span>
    </Stack>
  );
};

const TextCell = ({
  contentType,
  editable,
  errorMessage,
  onBlur,
  onCellChange,
  onClick,
  placeholder,
  value,
  valueComponentProps,
}) => {
  return (
    <TextCellContent
      contentType={contentType}
      editable={editable}
      errorMessage={errorMessage}
      onBlur={onBlur}
      onCellChange={onCellChange}
      onClick={onClick}
      placeholder={placeholder}
      value={value}
      valueComponentProps={valueComponentProps}
    />
  );
};

const DropdownCell = ({ displayOptions, editable, onCellChange, options, placeholder, value, valueComponentProps }) => {
  return (
    <DropdownSingleSelect
      className="w-full"
      displayOptions={displayOptions}
      hideLabel
      onChange={onCellChange}
      options={options}
      placeholder={placeholder}
      readOnly={!editable}
      selectedValue={value}
      setSelectedValue={() => {}}
      {...valueComponentProps}
    />
  );
};

const CustomCell = (props) => {
  const { cellType } = props;

  if (cellType === "downloadFile" || cellType === "downloadZipFile") return <DownloadCell {...props} />;

  return (
    <TableCell
      className={cn(
        !props?.onMouseEnter && (props?.editable ? "hover:bg-lds-selection-subtle" : "hover:bg-lds-selection-strong"),
        props?.valueClassName
      )}
      onClick={props?.onCellClick}
      onMouseEnter={props?.onMouseEnter}
    >
      <Stack className="w-full" spacing={3}>
        {props?.beforeTextIconName && (
          <Icon
            className={`before-text-icon -ml-2 ${props?.beforeTextIconClassName}`}
            iconName={props?.beforeTextIconName}
            onClick={props?.iconAction}
            size="md"
          />
        )}
        {cellType === "color" ? (
          <ColorCell {...props} />
        ) : cellType === "custom" ? (
          <span className="block w-full text-center">{props?.value}</span>
        ) : cellType === "dropdown" ? (
          <DropdownCell {...props} />
        ) : cellType === "autocomplete" ? (
          <AutocompleteCell {...props} />
        ) : (
          <TextCell {...props} />
        )}
      </Stack>
    </TableCell>
  );
};
const Wrapper = styled(TableContainer, {
  shouldForwardProp: (prop) => prop !== "containedScroll" && prop !== "rowHeight" && prop !== "rowRemoveable",
})(({ containedScroll, rowHeight = "4.8rem" }) => ({
  ".before-text-icon": {
    cursor: "pointer",
  },
  ".body-cell": {
    borderLeft: "1px solid var(--color-border-soft)",
    borderRight: "1px solid var(--color-border-soft)",
    height: rowHeight,
  },
  ".opacity-zero": {
    opacity: 0,
  },
  ".text-center": {
    textAlign: "center",
  },
  maxWidth: "100%",
  minWidth: 0,
  textAlign: "left",
  width: "100%",
  ...(containedScroll
    ? {
        flex: "1 1 0",
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        overflow: "auto",
      }
    : {
        overflowX: "auto",
      }),
}));
